/**
 * Academic Information Component
 * Displays and manages user's academic information
 */

import React, { useState, useEffect } from 'react';
import { GraduationCap, Book, Hash, Building, MapPin } from 'lucide-react';
import { universities } from '../../../core/constants/universities';

/**
 * Academic Information Component
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Academic information data
 * @param {boolean} props.isEditing - Whether the component is in edit mode
 * @param {Function} props.onChange - Function to handle data changes
 * @returns {JSX.Element} Academic information component
 */
const AcademicInformation = ({ data, isEditing, onChange }) => {
  const [formData, setFormData] = useState({
    university: '',
    degree: '',
    yearOfStudy: '',
    studentNumber: '',
    residence: '',
    faculty: '',
    major: '',
    minor: '',
    graduationYear: ''
  });
  
  // Year of study options
  const yearOptions = ['1', '2', '3', '4', '5', '6', '7', '8'];
  
  // Graduation year options
  const currentYear = new Date().getFullYear();
  const graduationYearOptions = Array.from({ length: 10 }, (_, i) => String(currentYear + i));
  
  // Residence type options
  const residenceOptions = [
    { value: 'on-campus', label: 'On-Campus Residence' },
    { value: 'off-campus', label: 'Off-Campus Housing' },
    { value: 'home', label: 'Living at Home' },
    { value: 'other', label: 'Other' }
  ];
  
  // Faculty options (could be expanded or made dynamic based on university)
  const facultyOptions = [
    'Science',
    'Engineering',
    'Commerce',
    'Humanities',
    'Law',
    'Medicine',
    'Arts',
    'Social Sciences',
    'Education',
    'Mathematics',
    'Business',
    'Computer Science',
    'Other'
  ];
  
  // Initialize form data
  useEffect(() => {
    if (data) {
      setFormData({
        university: data.university || '',
        degree: data.degree || '',
        yearOfStudy: data.yearOfStudy || '',
        studentNumber: data.studentNumber || '',
        residence: data.residence || '',
        faculty: data.faculty || '',
        major: data.major || '',
        minor: data.minor || '',
        graduationYear: data.graduationYear || ''
      });
    }
  }, [data]);
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    onChange({ [name]: value });
  };

  // Render edit mode
  if (isEditing) {
    return (
      <div className="space-y-6">
        {/* University Input */}
        <div>
          <label htmlFor="university" className="block text-sm font-medium text-gray-700 mb-1">
            University
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <GraduationCap className="h-5 w-5 text-gray-400" />
            </div>
            <select
              name="university"
              id="university"
              value={formData.university}
              onChange={handleInputChange}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
              disabled={true} // University cannot be changed after registration
            >
              <option value="">Select University</option>
              {universities.map((uni) => (
                <option key={uni.name} value={uni.name}>
                  {uni.name}
                </option>
              ))}
            </select>
          </div>
          <p className="mt-1 text-xs text-gray-500">University cannot be changed</p>
        </div>
        
        {/* Faculty Input */}
        <div>
          <label htmlFor="faculty" className="block text-sm font-medium text-gray-700 mb-1">
            Faculty
          </label>
          <select
            name="faculty"
            id="faculty"
            value={formData.faculty}
            onChange={handleInputChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
          >
            <option value="">Select Faculty</option>
            {facultyOptions.map((faculty) => (
              <option key={faculty} value={faculty}>
                {faculty}
              </option>
            ))}
          </select>
        </div>
        
        {/* Degree Program Input */}
        <div>
          <label htmlFor="degree" className="block text-sm font-medium text-gray-700 mb-1">
            Degree Program
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Book className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="degree"
              id="degree"
              value={formData.degree}
              onChange={handleInputChange}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
              placeholder="e.g. Bachelor of Science"
            />
          </div>
        </div>
        
        {/* Major Input */}
        <div>
          <label htmlFor="major" className="block text-sm font-medium text-gray-700 mb-1">
            Major
          </label>
          <input
            type="text"
            name="major"
            id="major"
            value={formData.major}
            onChange={handleInputChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="e.g. Computer Science"
          />
        </div>
        
        {/* Minor Input */}
        <div>
          <label htmlFor="minor" className="block text-sm font-medium text-gray-700 mb-1">
            Minor (Optional)
          </label>
          <input
            type="text"
            name="minor"
            id="minor"
            value={formData.minor}
            onChange={handleInputChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="e.g. Mathematics"
          />
        </div>
        
        {/* Year of Study Input */}
        <div>
          <label htmlFor="yearOfStudy" className="block text-sm font-medium text-gray-700 mb-1">
            Year of Study
          </label>
          <select
            name="yearOfStudy"
            id="yearOfStudy"
            value={formData.yearOfStudy}
            onChange={handleInputChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
          >
            <option value="">Select Year</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                Year {year}
              </option>
            ))}
          </select>
        </div>
        
        {/* Expected Graduation Year Input */}
        <div>
          <label htmlFor="graduationYear" className="block text-sm font-medium text-gray-700 mb-1">
            Expected Graduation Year
          </label>
          <select
            name="graduationYear"
            id="graduationYear"
            value={formData.graduationYear}
            onChange={handleInputChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
          >
            <option value="">Select Year</option>
            {graduationYearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        
        {/* Student Number Input */}
        <div>
          <label htmlFor="studentNumber" className="block text-sm font-medium text-gray-700 mb-1">
            Student Number
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Hash className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="studentNumber"
              id="studentNumber"
              value={formData.studentNumber}
              onChange={handleInputChange}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
              placeholder="Your student number"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">This will not be shared publicly</p>
        </div>
        
        {/* Residence Input */}
        <div>
          <label htmlFor="residence" className="block text-sm font-medium text-gray-700 mb-1">
            Residence Type
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Building className="h-5 w-5 text-gray-400" />
            </div>
            <select
              name="residence"
              id="residence"
              value={formData.residence}
              onChange={handleInputChange}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
            >
              <option value="">Select Residence Type</option>
              {residenceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  }

  // Render view mode
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-6">
        {/* University */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">University</h3>
          <p className="mt-1 text-base text-gray-900">{formData.university || 'Not specified'}</p>
        </div>
        
        {/* Faculty */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Faculty</h3>
          <p className="mt-1 text-base text-gray-900">{formData.faculty || 'Not specified'}</p>
        </div>
        
        {/* Degree Program */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Degree Program</h3>
          <p className="mt-1 text-base text-gray-900">{formData.degree || 'Not specified'}</p>
        </div>
        
        {/* Major */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Major</h3>
          <p className="mt-1 text-base text-gray-900">{formData.major || 'Not specified'}</p>
        </div>
        
        {/* Minor */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Minor</h3>
          <p className="mt-1 text-base text-gray-900">{formData.minor || 'Not specified'}</p>
        </div>
        
        {/* Year of Study */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Year of Study</h3>
          <p className="mt-1 text-base text-gray-900">
            {formData.yearOfStudy ? `Year ${formData.yearOfStudy}` : 'Not specified'}
          </p>
        </div>
        
        {/* Expected Graduation */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Expected Graduation</h3>
          <p className="mt-1 text-base text-gray-900">{formData.graduationYear || 'Not specified'}</p>
        </div>
        
        {/* Student Number */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Student Number</h3>
          <p className="mt-1 text-base text-gray-900">{formData.studentNumber || 'Not specified'}</p>
        </div>
        
        {/* Residence */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Residence Type</h3>
          <p className="mt-1 text-base text-gray-900">
            {residenceOptions.find(option => option.value === formData.residence)?.label || 'Not specified'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcademicInformation;